import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, History, BarChart3, Wallet, Loader2 } from 'lucide-react';
import GenerateTab from './generation/GenerateTab';
import HistoryTab from './generation/HistoryTab';
import StatsTab from './generation/StatsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { id: 'generate', label: 'Generate', icon: Sparkles },
  { id: 'history', label: 'History', icon: History },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
];

function BalancePill() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/generation/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalance(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Balance unavailable');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-zinc-800 bg-zinc-950"
      data-testid="generation-balance"
    >
      <Wallet className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">Balance</span>
        {loading && balance === null ? (
          <span className="text-sm text-zinc-400 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading...
          </span>
        ) : error ? (
          <span className="text-sm text-red-400" title={error}>—</span>
        ) : (
          <span className="text-base font-bold text-white">
            ${balance.usd_balance.toFixed(2)}
            <span className="text-xs text-zinc-500 ml-2 font-normal">
              ({balance.credits.toLocaleString()} credits)
            </span>
          </span>
        )}
      </div>
      <button
        onClick={load}
        className="text-xs text-zinc-500 hover:text-white transition"
        data-testid="refresh-balance-btn"
        title="Refresh"
      >
        ↻
      </button>
    </div>
  );
}

export default function Generation() {
  const [activeTab, setActiveTab] = useState('generate');

  const renderContent = () => {
    switch (activeTab) {
      case 'generate':
        return <GenerateTab onGenerated={() => setActiveTab('history')} />;
      case 'history':
        return <HistoryTab />;
      case 'stats':
        return <StatsTab />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="generation-title">
            Generation
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Generate images and videos with Nano Banana Pro, GPT Image-2 and Seedance 2.0
          </p>
        </div>
        <BalancePill />
      </div>

      <div className="border-b border-zinc-800">
        <nav className="flex gap-1 -mb-px overflow-x-auto" data-testid="generation-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`generation-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-white text-white'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {renderContent()}
    </div>
  );
}
